import { useState, useEffect, useCallback, useMemo } from 'react';

/**
 * Custom hook for debouncing values
 */
export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Custom hook for debounced search with results caching
 */
export function useDebouncedSearch<T>(
  searchFunction: (query: string) => Promise<T[]>,
  delay: number = 300,
  minQueryLength: number = 2
) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cache for search results
  const cache = useMemo(() => new Map<string, T[]>(), []);

  const debouncedQuery = useDebounce(query, delay);

  useEffect(() => {
    const performSearch = async () => {
      if (!debouncedQuery || debouncedQuery.length < minQueryLength) {
        setResults([]);
        setLoading(false);
        return;
      }

      // Check cache first
      if (cache.has(debouncedQuery)) {
        setResults(cache.get(debouncedQuery)!);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const searchResults = await searchFunction(debouncedQuery);
        
        // Cache results
        cache.set(debouncedQuery, searchResults);
        
        // Limit cache size to prevent memory leaks
        if (cache.size > 100) {
          const firstKey = cache.keys().next().value;
          if (firstKey) {
            cache.delete(firstKey);
          }
        }
        
        setResults(searchResults);
      } catch (err) {
        console.error('Search error:', err);
        setError(err instanceof Error ? err.message : 'Search failed');
        setResults([]);
      } finally {
        setLoading(false);
      }
    };

    performSearch();
  }, [debouncedQuery, searchFunction, cache, minQueryLength]);

  const clearResults = useCallback(() => {
    setQuery('');
    setResults([]);
    setError(null);
  }, []);

  const clearCache = useCallback(() => {
    cache.clear();
  }, [cache]);

  return {
    query,
    setQuery,
    results,
    loading,
    error,
    clearResults,
    clearCache,
  };
}

/**
 * Custom hook for debounced filters with change tracking
 */
export function useDebouncedFilters<T extends Record<string, any>>(
  initialFilters: T,
  onFiltersChange: (filters: T) => void,
  delay: number = 300
) {
  const [filters, setFilters] = useState<T>(initialFilters);
  const [hasChanges, setHasChanges] = useState(false);

  const debouncedFilters = useDebounce(filters, delay);

  // Call the callback when debounced filters change
  useEffect(() => {
    onFiltersChange(debouncedFilters);
  }, [debouncedFilters, onFiltersChange]);

  // Track if there are unsaved changes
  useEffect(() => {
    const hasUnsavedChanges = JSON.stringify(filters) !== JSON.stringify(debouncedFilters);
    setHasChanges(hasUnsavedChanges);
  }, [filters, debouncedFilters]);

  const updateFilter = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const updateFilters = useCallback((newFilters: Partial<T>) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters,
    }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(initialFilters);
  }, [initialFilters]);

  const clearFilter = useCallback(<K extends keyof T>(key: K) => {
    setFilters(prev => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
  }, []);

  const applyFiltersImmediately = useCallback(() => {
    onFiltersChange(filters);
  }, [filters, onFiltersChange]);

  return {
    filters,
    debouncedFilters,
    hasChanges,
    updateFilter,
    updateFilters,
    resetFilters,
    clearFilter,
    applyFiltersImmediately,
  };
}

/**
 * Custom hook for debounced API calls with caching and loading states
 */
export function useDebouncedApi<T, P extends any[]>(
  apiFunction: (...params: P) => Promise<T>,
  delay: number = 300,
  cacheResults: boolean = true
) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastParams, setLastParams] = useState<P | null>(null);

  // Cache for API results
  const cache = useMemo(() => new Map<string, T>(), []);

  const debouncedParams = useDebounce(lastParams, delay);

  useEffect(() => {
    const makeApiCall = async () => {
      if (!debouncedParams) return;

      const cacheKey = cacheResults ? JSON.stringify(debouncedParams) : '';
      
      // Check cache first
      if (cacheResults && cache.has(cacheKey)) {
        setData(cache.get(cacheKey)!);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const result = await apiFunction(...debouncedParams);
        
        // Cache result
        if (cacheResults) {
          cache.set(cacheKey, result);
          
          // Limit cache size
          if (cache.size > 50) {
            const firstKey = cache.keys().next().value;
            if (firstKey) {
              cache.delete(firstKey);
            }
          }
        }
        
        setData(result);
      } catch (err) {
        console.error('API call error:', err);
        setError(err instanceof Error ? err.message : 'API call failed');
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    makeApiCall();
  }, [debouncedParams, apiFunction, cache, cacheResults]);

  const execute = useCallback((...params: P) => {
    setLastParams(params);
  }, []);

  const executeImmediately = useCallback(async (...params: P) => {
    const cacheKey = cacheResults ? JSON.stringify(params) : '';
    
    // Check cache first
    if (cacheResults && cache.has(cacheKey)) {
      setData(cache.get(cacheKey)!);
      return cache.get(cacheKey)!;
    }

    try {
      setLoading(true);
      setError(null);
      
      const result = await apiFunction(...params);
      
      // Cache result
      if (cacheResults) {
        cache.set(cacheKey, result);
      }
      
      setData(result);
      return result;
    } catch (err) {
      console.error('API call error:', err);
      setError(err instanceof Error ? err.message : 'API call failed');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [apiFunction, cache, cacheResults]);

  const clearCache = useCallback(() => {
    cache.clear();
  }, [cache]);

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setLoading(false);
    setLastParams(null);
  }, []);

  return {
    data,
    loading,
    error,
    execute,
    executeImmediately,
    clearCache,
    reset,
  };
}

/**
 * Custom hook for optimistic updates with rollback capability
 */
export function useOptimisticUpdate<T>(
  initialData: T,
  updateFunction: (data: T) => Promise<T>
) {
  const [data, setData] = useState<T>(initialData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = useCallback(async (optimisticData: T) => {
    const previousData = data;
    
    try {
      // Apply optimistic update immediately
      setData(optimisticData);
      setLoading(true);
      setError(null);
      
      // Perform actual update
      const result = await updateFunction(optimisticData);
      setData(result);
    } catch (err) {
      // Rollback on error
      setData(previousData);
      setError(err instanceof Error ? err.message : 'Update failed');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [data, updateFunction]);

  const reset = useCallback(() => {
    setData(initialData);
    setError(null);
    setLoading(false);
  }, [initialData]);

  return {
    data,
    loading,
    error,
    update,
    reset,
  };
}